import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Search, Edit, Trash2, ArrowRight, Building2, MapPin, Phone, User, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Branch } from '../../types';
import { useAppContext } from '../../hooks/useAppContext';
import { useForm, SubmitHandler } from 'react-hook-form';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { EmptyState } from '../../components/common/EmptyState';

type BranchInputs = Omit<Branch, 'id' | 'creationDate'>;

const ModernBranchFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    branch: Branch | null;
    onSave: (data: BranchInputs | (BranchInputs & { id: string })) => void;
}> = ({ isOpen, onClose, branch, onSave }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm<BranchInputs>();

    useEffect(() => {
        if (branch) {
            reset(branch);
        } else {
            reset({ name: '', location: '', phone: '', manager: '' });
        }
    }, [branch, isOpen, reset]);

    const onSubmit: SubmitHandler<BranchInputs> = data => {
        if (branch) {
            onSave({ ...data, id: branch.id });
        } else {
            onSave(data);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="glass-panel w-full max-w-lg p-6 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <h2 className="text-xl font-bold text-white">{branch ? 'تعديل فرع' : 'إضافة فرع جديد'}</h2>
                        <button type="button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                            <span className="sr-only">Close</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">اسم الفرع</label>
                            <div className="relative">
                                <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <input
                                    {...register("name", { required: "اسم الفرع مطلوب" })}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-2 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="اسم الفرع"
                                />
                            </div>
                            {errors.name && <p className="text-xs text-red-400">{errors.name.message}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">موقع الفرع</label>
                            <div className="relative">
                                <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <input
                                    {...register("location", { required: "الموقع مطلوب" })}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-2 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="الموقع"
                                />
                            </div>
                            {errors.location && <p className="text-xs text-red-400">{errors.location.message}</p>}
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">هاتف الفرع</label>
                            <div className="relative">
                                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <input
                                    {...register("phone")}
                                    type="tel"
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-2 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="رقم الهاتف"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">اسم المدير</label>
                            <div className="relative">
                                <User className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <input
                                    {...register("manager")}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-2 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="اسم المدير"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all"
                        >
                            حفظ
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ModernManageBranchesScreen: React.FC = () => {
    const { t } = useAppContext();
    const {
        branches,
        openConfirmation,
        addBranch,
        updateBranch,
        deleteBranch,
    } = useAppStore();
    const navigate = useNavigate();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSaveBranch = (data: BranchInputs | (BranchInputs & { id: string })) => {
        if ('id' in data) {
            const originalBranch = branches.find(b => b.id === data.id);
            if (originalBranch) {
                updateBranch({ ...data, creationDate: originalBranch.creationDate });
                toast.success('تم تحديث الفرع بنجاح!');
            }
        } else {
            addBranch({ ...data, creationDate: new Date().toISOString() });
            toast.success('تم إنشاء الفرع بنجاح!');
        }
        setModalOpen(false);
        setEditingBranch(null);
    }

    const handleAddClick = () => {
        setEditingBranch(null);
        setModalOpen(true);
    };

    const handleEditClick = (branch: Branch) => {
        setEditingBranch(branch);
        setModalOpen(true);
    };

    const handleDeleteClick = (branchId: string) => {
        openConfirmation('هل أنت متأكد من حذف هذا الفرع؟ لا يمكن التراجع عن هذا الإجراء.', () => {
            deleteBranch(branchId);
            toast.success('تم حذف الفرع بنجاح!');
        });
    };

    const filteredBranches = branches.filter(branch =>
        branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.manager.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{t('manageBranches')}</h1>
                    <p className="text-slate-400">إدارة فروع الشركة ومواقعها</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span>إضافة فرع</span>
                    </button>
                </div>
            </div>

            {/* Search and Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-6 rounded-2xl md:col-span-2">
                    <div className="relative">
                        <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
                        <input
                            type="text"
                            placeholder="بحث عن فرع..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-12 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي الفروع</p>
                        <p className="text-3xl font-bold text-white">{branches.length}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary">
                        <Building2 size={24} />
                    </div>
                </div>
            </div>

            {/* Branches Grid */}
            {filteredBranches.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBranches.map((branch) => (
                        <div key={branch.id} className="glass-card p-6 rounded-2xl group hover:border-primary/30 transition-all duration-300">
                            <div className="flex justify-between items-start mb-4">
                                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-white/5 flex items-center justify-center text-slate-300 group-hover:text-primary group-hover:border-primary/30 transition-colors">
                                    <Building2 size={24} />
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleEditClick(branch)}
                                        className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(branch.id)}
                                        className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-4">{branch.name}</h3>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-slate-400">
                                    <MapPin size={16} className="text-primary/70" />
                                    <span className="text-sm">{branch.location}</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-400">
                                    <User size={16} className="text-purple-400/70" />
                                    <span className="text-sm">{branch.manager || 'غير محدد'}</span>
                                </div>
                                {branch.phone && (
                                    <div className="flex items-center gap-3 text-slate-400">
                                        <Phone size={16} className="text-green-400/70" />
                                        <span className="text-sm">{branch.phone}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-3 text-slate-400 pt-2 border-t border-white/5 mt-2">
                                    <Calendar size={16} className="text-slate-500" />
                                    <span className="text-xs">تاريخ الإنشاء: {new Date(branch.creationDate).toLocaleDateString('ar-SA')}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyState
                    icon={Building2}
                    title="لا توجد فروع"
                    message={searchTerm ? "لا توجد نتائج مطابقة للبحث" : "لم يتم إضافة أي فرع حتى الآن. ابدأ بإضافة فرع جديد."}
                    action={!searchTerm ? <Button icon={<PlusCircle size={18} />} onClick={handleAddClick}>إضافة فرع</Button> : undefined}
                />
            )}

            <ModernBranchFormModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                branch={editingBranch}
                onSave={handleSaveBranch}
            />
        </div>
    );
};

export default ModernManageBranchesScreen;
